import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import i18n from '../../utils/i18n';
import FormField from '../common/FormField';
import {TEMPLATE_CATEGORY_FIELD_NAMES} from '../../constants/issue-templates';
import {NotificationLevels, NotificationTypes} from '@adsk/bim360-matrix-react-notification-manager/es/helpers/consts';
import Button from '@adsk/bim360-matrix-react-button';
import InputValidationView from '@adsk/bim360-matrix-react-input-validation-view';
import TextFieldCore from '@adsk/bim360-matrix-react-text-field-core';
import {templateCategoryType} from '../../constants/proptype-validation';
import {getCategoryTitleValidationMessage} from '../../utils/validation-helpers';
import {useNewValueWithPristineFlag} from '../common/formHooks';

const TemplateCategoryCreatePopup = ({
  templateCategoryTitles,
  container,
  isOpen,
  closePopup,
  onCreateSuccess,
  createTemplateCategoryAction,
  showNotificationAction
}) => {
  const [title, onChange, isPristine, resetField] = useNewValueWithPristineFlag();
  const [isSaving, setIsSaving] = useState(false);
  const titleRef = useRef(null);
  const message = getCategoryTitleValidationMessage(title, templateCategoryTitles);
  const showFieldError = !isPristine && !!message;

  useEffect(() => {
    if (isOpen) {
      titleRef.current && titleRef.current.focus();
    }
  }, [isOpen]);

  const onClose = useCallback(() => {
    closePopup();
    resetField();
  }, [closePopup, resetField]);

  const cancelButtonText = i18n.t('navigation.cancel');
  const saveButtonText = isSaving ? i18n.t('new.creating') : i18n.t('new.create');
  const saveButtonClasses = cx('TemplateCategoryCreatePopup__save-button', {
    'TemplateCategoryCreatePopup__save-button--saving': isSaving
  });
  const label = i18n.t('createCategory.title.label');
  const placeholder = i18n.t('createCategory.title.placeholder');

  const onSubmit = useCallback(() => {
    if (!showFieldError) {
      setIsSaving(true);
      createTemplateCategoryAction(
        {
          [TEMPLATE_CATEGORY_FIELD_NAMES.TITLE]: title
        },
        {
          onSuccess: category => {
            setIsSaving(false);
            onCreateSuccess && onCreateSuccess(category);
            onClose();
            showNotificationAction({
              type: NotificationTypes.TOAST,
              level: NotificationLevels.SUCCESS,
              message: i18n.t('templatesGrid.newModal.createdCategoryMessage')
            });
          },
          onError: () => {
            setIsSaving(false);
            showNotificationAction({
              type: NotificationTypes.SNACKBAR,
              level: NotificationLevels.ERROR,
              message: i18n.t('templatesGrid.newModal.createdCategoryErrorMessage')
            });
          }
        }
      );
    }
  }, [createTemplateCategoryAction, showFieldError, onClose, onCreateSuccess, showNotificationAction, title]);

  return (
    <div className="TemplateCategoryCreatePopup" data-testid="TemplateCategoryCreatePopup">
      <div className="TemplateCategoryCreatePopup__content" data-testid="TemplateCategoryCreatePopup__content">
        <FormField
          className="TemplateCategoryCreatePopup__form-field"
          labelText={label}
          showFieldError={showFieldError}
          errorMessageText={message}
          inputValidationViewType={InputValidationView.Types.POPOVER}
          container={container}
        >
          <TextFieldCore
            ref={titleRef}
            valid={!showFieldError}
            value={title}
            onChange={onChange}
            disabled={isSaving}
            placeholder={placeholder}
          />
        </FormField>
      </div>
      <div className="TemplateCategoryCreatePopup__footer">
        <Button
          styleType={Button.StyleType.LINK_SECONDARY}
          onClick={onClose}
          disabled={isSaving}
          data-testid="TemplateCategoryCreatePopup__cancel-button"
        >
          {cancelButtonText}
        </Button>
        <Button
          styleType={Button.StyleType.PRIMARY}
          data-testid="TemplateCategoryCreatePopup__save-button"
          className={saveButtonClasses}
          onClick={onSubmit}
          disabled={isSaving}
        >
          {saveButtonText}
        </Button>
      </div>
    </div>
  );
};

TemplateCategoryCreatePopup.displayName = 'TemplateCategoryCreatePopup';

TemplateCategoryCreatePopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closePopup: PropTypes.func.isRequired,
  onCreateSuccess: PropTypes.func,
  createTemplateCategoryAction: PropTypes.func.isRequired,
  showNotificationAction: PropTypes.func.isRequired,
  templateCategoryTitles: PropTypes.arrayOf(templateCategoryType).isRequired,
  container: PropTypes.object
};

export default TemplateCategoryCreatePopup;
